import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import {
  useTags,
  useUpdateTag,
  useDeleteTag,
} from '../features/tags';
import { fetchCloudMaterials, fetchTemporaryMaterials } from '../features/materials/api';
import { useMaterialsPanelStore } from '../stores/materialsPanelStore';
import { Button } from '../components/common/Button';
import { CreateButton } from '../components/common/CreateButton';
import { useConfirmDialog } from '../components/common/useConfirmDialog';
import { TagColorPicker } from '../components/tags/TagColorPicker';
import { CreateTagModal } from '../components/tags/CreateTagModal';
import { brandConfig } from '../config/brands';
import '../components/common/CommonTable.css';
import '../styles/inline-edit.css';
import './TagsManagementPage.css';

type Folder = 'shared' | 'personal';

function TagMaterialCount({
  tagId,
  folder,
  onClick,
}: {
  tagId: string;
  folder: Folder;
  onClick: (folder: Folder) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['tag-material-count', folder, tagId],
    queryFn: async () => {
      const fetcher = folder === 'shared' ? fetchCloudMaterials : fetchTemporaryMaterials;
      const res = await fetcher({ limit: 1, filters: { tag_ids: [tagId] } });
      return res.total_count ?? 0;
    },
    staleTime: 60_000,
  });

  if (isLoading) return <span className="count-pill count-pill--loading">…</span>;
  const count = data ?? 0;
  // 個人フォルダは素材パネルの個人タブと同じサブカラー
  const variant = folder === 'personal' ? ' count-pill--personal' : '';
  return (
    <button
      type="button"
      className={`count-pill${variant} ${count === 0 ? 'count-pill--zero' : ''}`}
      onClick={() => onClick(folder)}
      disabled={count === 0}
    >
      {count.toLocaleString()}
    </button>
  );
}

export default function TagsManagementPage() {
  const { confirm, dialog } = useConfirmDialog();
  const openWithTagFilter = useMaterialsPanelStore((state) => state.openWithTagFilter);
  const tagsQuery = useTags();

  const openMaterialsForTag = (tagId: string, folder: Folder) => {
    openWithTagFilter(tagId, folder);
  };
  const updateTagMutation = useUpdateTag();
  const deleteTagMutation = useDeleteTag();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // タグ名のインライン編集
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const startEditName = (tag: { id: string; name: string }) => {
    setEditingTagId(tag.id);
    setEditingName(tag.name);
  };

  const cancelEditName = () => {
    setEditingTagId(null);
    setEditingName('');
  };

  const saveEditName = async (tag: { id: string; name: string }) => {
    const next = editingName.trim();
    if (!next || next === tag.name) {
      cancelEditName();
      return;
    }
    try {
      await updateTagMutation.mutateAsync({ tagId: tag.id, payload: { name: next } });
      cancelEditName();
    } catch (error) {
      console.error('Failed to update tag name:', error);
    }
  };

  const handleUpdateTagColor = async (tagId: string, color: string) => {
    try {
      await updateTagMutation.mutateAsync({ tagId, payload: { color } });
    } catch (error) {
      console.error('Failed to update tag color:', error);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    const ok = await confirm({
      title: `この${brandConfig.labels.tag}を削除しますか？`,
      confirmLabel: '削除する',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await deleteTagMutation.mutateAsync(tagId);
    } catch (error) {
      console.error('Failed to delete tag:', error);
    }
  };

  return (
    <div className="page admin-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <img src="/tags_1.svg" alt="" className="page-title-icon" />
            {brandConfig.labels.tagManagement}
          </h1>
          <p className="page-description">
            {brandConfig.labels.material}に付与する{brandConfig.labels.tag}を管理します。{brandConfig.labels.tag}を使用して{brandConfig.labels.material}を分類・整理できます。
          </p>
        </div>
        <div className="page-header-actions">
          <CreateButton onClick={() => setIsCreateModalOpen(true)}>
            {`新規${brandConfig.labels.tag}作成`}
          </CreateButton>
        </div>
      </div>

      {tagsQuery.isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>{brandConfig.labels.tag}を読み込み中...</p>
        </div>
      )}

      {tagsQuery.isError && (
        <div className="error-state">
          <p>{brandConfig.labels.tag}の読み込みに失敗しました</p>
        </div>
      )}

      {tagsQuery.data && tagsQuery.data.length === 0 && (
        <div className="empty-state">
          <p>{brandConfig.labels.tag}が登録されていません</p>
          <p className="empty-hint">「新規{brandConfig.labels.tag}作成」ボタンから最初の{brandConfig.labels.tag}を作成してください</p>
        </div>
      )}

      {tagsQuery.data && tagsQuery.data.length > 0 && (
        <div className="table-container">
          <table className="tags-table common-table">
            <thead>
              <tr>
                <th className="col-name">{brandConfig.labels.tag}名</th>
                <th className="col-color">カラー</th>
                <th className="col-count">共有フォルダ</th>
                <th className="col-count">個人フォルダ</th>
                <th className="col-actions">アクション</th>
              </tr>
            </thead>
            <tbody>
              {tagsQuery.data.map((tag) => (
                <tr key={tag.id}>
                  <td className="col-name">
                    {editingTagId === tag.id ? (
                      <div className="inline-edit-container">
                        <input
                          type="text"
                          className="inline-edit-input"
                          value={editingName}
                          autoFocus
                          maxLength={50}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void saveEditName(tag);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditName();
                            }
                          }}
                          aria-label={`${tag.name}の名前を編集`}
                        />
                        <button
                          type="button"
                          onClick={() => void saveEditName(tag)}
                          className="inline-save-button"
                          disabled={updateTagMutation.isPending}
                          title="保存"
                        >
                          <Check size={18} className="inline-action-icon" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditName}
                          className="inline-cancel-button"
                          title="キャンセル"
                        >
                          <X size={18} className="inline-action-icon" />
                        </button>
                      </div>
                    ) : (
                      <div className="inline-display">
                        <span className="tag-name">{tag.name}</span>
                        <button
                          type="button"
                          onClick={() => startEditName(tag)}
                          className="inline-edit-button"
                          title="編集"
                        >
                          <img src="/pencil_1.svg" alt="編集" className="inline-edit-icon" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="col-color">
                    <TagColorPicker
                      value={tag.color}
                      onChange={(color) => handleUpdateTagColor(tag.id, color)}
                      disabled={updateTagMutation.isPending}
                      ariaLabel={`${tag.name} のカラーを選択`}
                    />
                  </td>
                  <td className="col-count">
                    <TagMaterialCount
                      tagId={tag.id}
                      folder="shared"
                      onClick={(folder) => openMaterialsForTag(tag.id, folder)}
                    />
                  </td>
                  <td className="col-count">
                    <TagMaterialCount
                      tagId={tag.id}
                      folder="personal"
                      onClick={(folder) => openMaterialsForTag(tag.id, folder)}
                    />
                  </td>
                  <td className="col-actions">
                    <div className="actions-group">
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => void handleDeleteTag(tag.id)}
                        disabled={deleteTagMutation.isPending}
                        aria-label={`${tag.name}を削除`}
                      >
                        <img src="/bin_1.svg" alt="" aria-hidden="true" style={{ width: '14px', height: '14px' }} />
                        削除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateTagModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
      {dialog}
    </div>
  );
}
